import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { ENV_BUILD, environment } from "src/environments/environment";
import { forkJoin, Observable, of } from "rxjs";
import { map, switchMap, tap, catchError } from "rxjs/operators";
import * as moment from "moment";
import {
  AccessOptions,
  GroupAnnouncers,
  GroupDetail,
  GroupMembershipOption,
  GroupMembershipStatus,
  GroupRequester,
  GroupSearchResult,
  GroupSettings,
  GroupVisibility,
  GroupVisibilityStatus,
  IdenediType,
  MemberListVisibility
} from "src/app/groups/models/group-detail.model";
import { finalize } from 'rxjs/operators';
import { AuthService, AuthType, ESP_LOGIN_DATA } from "src/app/shared/services/auth.service";
import { Profile } from "src/app/shared/models/profile.model";
import { UserBasicProfile } from "src/app/shared/models/user-basic-profile.mode";
import { SmartContractTemplateModel } from "../shared/models/smartcontract.model";
import { APP_DEFAULTS } from "../shared/models/app-defaults";
import { AppMsalConfig } from "../../app-msal-config";
import { AlertService } from "../shared/alert/alert.service";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { CountryNotAvailableDialogComponent } from "./country-not-available-dialog/country-not-available-dialog.component";
import { AdminRole, SubGroupType } from "../shared/enums/enums";
import { ExdrLanguageService } from "../languages/services/exdr-language.service";
import { AuthService as PlannexeAuthService } from "../plannexe/shared/services/auth.service";
import { LocalEnum } from "../plannexe/shared/enums/localstorage";
import { LocalEnum as SellexeLocalEnum } from "src/app/sellexe/shared/enums/localstorage";
// import * as html2pdf from 'html2pdf.js';

const STEMEXE_APP_ID = "stemexe";
const EXCEED_IDENEDI_GROUP_ID_KEY = "exceed.user.idenedi.group.id";
const VALID_MEMBERSHIP_OPTIONS = [
  GroupMembershipOption.AdminApprovalRequired,
  GroupMembershipOption.AnyoneCanJoin,
  GroupMembershipOption.JoinByEmailAddressDomain
];

@Injectable({
  providedIn: "root"
})
export class GroupsService {
  private GO_BACK_KEY = 'GROUP_GO_BACK_URL';
  tenantDataForLogin: GroupDetail[] = [];
  groupAdmin: boolean = false;
  private _groupFullName: string = '';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private langService: ExdrLanguageService,
    private alert: AlertService,
    private dialog: MatDialog,
    private plannexeAuthService: PlannexeAuthService,
  ) { }

  createNewGroup(
    groupName: string,
    memberIdenedis?: string[]
  ): Observable<{
    str: string;
  }> {
    const url = `${environment.apiHost}/api/Group`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    const body: {
      GroupName: string;
      MemberIdenedis?: string[];
    } = {
      GroupName: groupName
    };
    if (memberIdenedis) {
      body.MemberIdenedis = memberIdenedis;
    }
    return this.http.post<any>(url, body, options);
  }

  checkCountDown() {
    const url = environment.countDown;
    const options = {
      // headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.get<string[]>(url, options);
  }

  checkIfSubTenantsExist(groupId: string): Observable<boolean> {
    return this.fetchSubGroups(groupId, true, SubGroupType.SubTenant).pipe(
      tap(subgroups => {
        // Set the tenant data
        this.tenantDataForLogin = subgroups;
        console.log("Data returned.....", this.tenantDataForLogin);
      }),
      map(subgroups => subgroups.length > 0)
    );
  }

  getSubTenantsForLogin() {
    if (Array.isArray(this.tenantDataForLogin) && this.tenantDataForLogin.length > 0) {
      // Return the tenant data if it exists
      console.log('Returning existing tenant data:', this.tenantDataForLogin);
      return this.tenantDataForLogin;
    } else {
      // If no subTenants, attempt to fetch them
      console.log('SubTenants array is empty, fetching data again...');
      this.checkIfSubTenantsExist(this.authService.idenediGroupId).subscribe(
        (hasSubtenants) => {
          if (hasSubtenants) {
            console.log('SubTenants successfully fetched:', this.tenantDataForLogin);
          } else {
            console.log('No subTenants found.');
          }
        },
        (error) => {
          console.error('Error fetching subTenants:', error);
        }
      );
    }
    return this.tenantDataForLogin
  }

  searchGroups(
    searchText: string,
    skip: number = 0,
    top: number = 100
  ): Observable<GroupDetail[]> {
    if (!searchText) {
      return of([]);
    }
    const url = `${environment.searchHost}/api/search/groups`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          skip: skip + "",
          top: top + "",
          query: searchText,
          orderby: "firstName",
          filter: "groupApps/any(app: app eq 'stemexe')"
        }
      })
    };
    return this.http.get(url, options).pipe(
      map((data: any) => {
        const serverValues: any[] = data.value || [];
        return serverValues
          .filter(grp => {
            return VALID_MEMBERSHIP_OPTIONS.includes(grp.groupMembershipOption);
          })
          .map(grp => {
            const searchGroup: GroupDetail = new GroupDetail();
            searchGroup.IdenediType = IdenediType.Group;
            searchGroup.Idenedi = grp.idenedi;

            searchGroup.GroupSettings = new GroupSettings();
            searchGroup.GroupSettings.MemberListVisibility =
              grp.groupMemberListVisibility;
            searchGroup.GroupSettings.GroupMembershipOption =
              grp.groupMembershipOption;
            searchGroup.GroupSettings.GroupJoinByInvitationOnly =
              grp.groupJoinByInviteOnly;

            searchGroup.Profile = new Profile();
            searchGroup.Profile.FirstName = grp.firstName;
            searchGroup.Profile.LastName = grp.lastName;

            if (grp.profileImageUrl && grp.profileImageUrl !== "") {
              searchGroup.Profile.isProfileImageAvailable = true;
              searchGroup.Profile.ProfileImageUrl = grp.profileImageUrl;
              searchGroup.Profile.ProfileImageSmallUrl = grp.profileImageUrl.replace(
                "profileimages",
                "profileimages-small"
              );
            } else {
              searchGroup.Profile.ProfileImageUrl =
                "./assets/images/group_img_default.png";
              searchGroup.Profile.ProfileImageSmallUrl =
                "./assets/images/group_img_default_small.png";
            }

            searchGroup.GroupSearchResult = new GroupSearchResult();
            // searchGroup.GroupSearchResult.JoinByInviteOnly = grp.groupJoinByInviteOnly
            searchGroup.GroupSearchResult.MembershipOptionData =
              grp.groupMembershipOptionData;
            // TODO: make it proper records
            // searchGroup.GroupSearchResult.PublicRecords = grp.PublicRecords
            return searchGroup;
          });
      })
    );
  }

  searchAnotherGroups(
    searchText: string,
    skip: number = 0,
    top: number = 100
  ): Observable<GroupDetail[]> {
    return forkJoin([this.getUserGroups(), this.searchGroups(searchText)]).pipe(
      map(([userGroups, foundGroups]) => {
        return foundGroups.filter(group => {
          return !userGroups.some(userGroupId => (userGroupId === group.Idenedi) || group?.GroupSettings?.GroupJoinByInvitationOnly);
        });
      })
    );
  }

  joinGroup(groupId: string, email?: string, invitationkey?: string): Observable<string> {
    let url = `${environment.apiHost}/api/group/${groupId}/membership`;
    let params = {};
    if (email) {
      params['emailAddress'] = email;
      params['isVerificationCode'] = true;
    }
    if (invitationkey) {
      params['inviteLinkKey'] = invitationkey;
    }
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: params

    };
    return this.http.post<string>(url, null, options)
      .pipe(
        catchError(err => {
          if (err?.error?.IdenediExceptionType == 'NOT_ALLOWED_COUNTRY') {
            this.dialog.open(CountryNotAvailableDialogComponent, {
              autoFocus: false,
              direction: this.langService.currentLanguage.isRtl ? 'rtl' : 'ltr',
              data: {}
            }).afterClosed().subscribe(val => {
              if (environment.singleGroupApp) {
                this.authService.logout();
              }
            })
          }
          throw err;
        }
        ));
  }
  sendEmailCode(email: string): Observable<string> {
    let url = `${environment.apiHost}/api/user/email/key`;
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Content-Type", "application/json")
    }
    return this.http.post<string>(url, `"${email}"`, options);
  }

  sendPhoneNumberCode(obj: any): Observable<any> {
    let url = `${environment.apiHost}/api/user/phone/code`;
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Content-Type", "application/json")
    }
    return this.http.post<any>(url, obj, options);
  }

  verifyPassword(password: string) {
    let url = `${environment.idenediProviderUrl}/api/account/VerifyPassword`;
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Content-Type", "application/json")
    };
    const passwordObj = {
      Password: password
    };
    return this.http.post<any>(url, passwordObj, options);
  }

  verifyEmailCode(groupId: string, email: string, code: string, inviteLinkKey?: string): Observable<string> {
    let url = `${environment.apiHost}/api/group/${groupId}/membership`;
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    let params = {};
    if (inviteLinkKey) {
      params['inviteLinkKey'] = inviteLinkKey;
    }
    const options = {
      headers: authHeaders, //.append("Idenedi-Api-Version", "5"),
      params: params
    }
    const data = {
      Email: email,
      Key: code
    }
    return this.http.post<string>(url, data, options);
  }
  getUserGroups(): Observable<string[]> {
    const url = `${environment.apiHost}/api/app/${STEMEXE_APP_ID}/group`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.get<string[]>(url, options);
  }

  getUserGroupsDetails(): Observable<GroupDetail[]> {
    return this.getUserGroups().pipe(
      switchMap(groupsIds => {
        if (!groupsIds || !groupsIds.length) {
          return of([]);
        }
        return this.getGroupsDetails(groupsIds);
      })
    );
  }

  getUserGroupsProfiles(groupsIds: string[], allList = false): Observable<UserBasicProfile> {
    const url = `${environment.apiHost}/api/user/basic`;
    let headers = new HttpHeaders();
    if (this.authService.isLoggedIn()) {
      headers = this.authService.buildAuthHeader(AuthType.Idenedi)
    }
    const options = {
      headers: headers,
      params: new HttpParams({
        fromObject: {
          idenedis: groupsIds.join(",")
        }
      })
    };

    let request$ = window["GROUP_INFO"]
      ? of(JSON.parse(decodeURIComponent(escape(atob(window['GROUP_INFO'])))))
      : this.http.get<UserBasicProfile[]>(url, options).pipe(map((profiles) => (profiles?.length ? (allList ? profiles : profiles[0]) : null)));

    return request$;
  }

  getGroupDetails(groupId: string): Observable<GroupDetail> {
    // const url = `${environment.apiHost}/api/member/${groupId}`;
    // const options = {
    //   headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    // };
    // // const url = `assets/mock/group.json`;
    // // TODO uncomment before commit
    // return this.http.get<GroupDetail>(url, options);
    return this.getGroupDetailsPublic(groupId);
  }

  getGroupIdByDomain(doamin: string): Observable<string> {
    const url = `${environment.apiHost}/api/reverselookup/CustomDomain?value=${doamin}`;
    return this.http.get<string>(url);
  }

  getGroupDetailsPublic(groupId: string): Observable<GroupDetail> {
    // Keeps updating the group id to subgroup id whenever we go into the details of a subgroup
    // if (groupId != null) {
    //   localStorage.setItem(EXCEED_IDENEDI_GROUP_ID_KEY, groupId);
    // }
    const url = `${environment.apiHost}/api/user/${groupId}`;
    let headers = new HttpHeaders();
    if (this.authService.isLoggedIn()) {
      headers = this.authService.buildAuthHeader(AuthType.Idenedi)
    }
    const options = {
      headers: headers
    };
    // Donot make this call if group id is null.
    if (groupId) {
      let request$ = ((window["GROUP_INFO_FULL"] && groupId === this.authService.idenediGroupId && !this.authService.isLoggedIn())
        ? of(JSON.parse(decodeURIComponent(escape(atob(window['GROUP_INFO_FULL'])))))
        : this.http.get<GroupDetail>(url, options))
        .pipe(map(group => {
          this.authService.loginRequired = group?.GroupSettings?.GroupApp?.LoginRequired ?? false;

          // Only set this.groupAdmin if groupId is not a subgroup id
          if (groupId === this.authService.idenediGroupId) {
            this.groupAdmin = group.IsAdmin;
            this._groupFullName = group?.Profile?.FirstName || '';
          }
          if (group.GroupMembershipStatus == GroupMembershipStatus.Admin) {
            group.GroupAdminRoles = (group.GroupAdminRoles?.length) ? group.GroupAdminRoles : [AdminRole.FullControl];
          }
          if (!group.GroupAdminRoles) {
            group.GroupAdminRoles = [];
          }
          return group;
        }));

      return request$;
    }
  }

  isAllowedPostCreating(): Observable<boolean> {
    if (!this.authService.isLoggedIn()) {
      return of(false);
    }

    return this.getGroupDetails(this.authService.idenediGroupId).pipe(
      map(group => {
        return (
          group &&
          (group.GroupMembershipStatus == GroupMembershipStatus.Admin ||
            group.GroupMembershipStatus == GroupMembershipStatus.Member) &&
          (group.GroupSettings.GroupAnnouncers == "AllMembers" ||
            (group.IsAdmin &&
              group.GroupSettings.GroupAnnouncers == "AdminsOnly"))
        );
      })
    );
  }

  getGroupRequesters(groupId: string): Observable<GroupRequester[]> {
    const url = `${environment.apiHost}/api/group/${groupId}/request`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http
      .get<GroupRequester[]>(url, options)
      .pipe(map(requesters => requesters.map(req => new GroupRequester(req))));
  }

  acceptRequesters(groupId: string, requestersIds: string[]): Observable<any> {
    const url = `${environment.apiHost}/api/group/${groupId}/request/accept`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<any>(url, requestersIds, options);
  }

  ignoreRequesters(groupId: string, requestersIds: string[]): Observable<any> {
    const url = `${environment.apiHost}/api/group/${groupId}/request/ignore`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<GroupRequester[]>(url, requestersIds, options);
  }

  getGroupDetailsMock(groupId: string): Observable<GroupDetail> {
    return this.http.get<GroupDetail>("assets/mock/group.json");
  }

  getGroupsDetails(groupsIds: string[]): Observable<GroupDetail[]> {
    const url = `${environment.apiHost}/api/member`;
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders, //.append("Idenedi-Api-Version", "4"),
      params: new HttpParams({
        fromObject: {
          idenedis: groupsIds.join(","),
          basicInfoOnly: true
        }
      })
    };
    return this.http.get<GroupDetail[]>(url, options).pipe(
      map(groups => this.mergeSubGroups(groups)),
      map(mergedGroups => this.sortGroupsByName(mergedGroups))
    );
  }

  changeGroupVisibility(
    groupId: string,
    groupVisibilityStatus: GroupVisibilityStatus
  ): Observable<void> {
    const url = `${environment.apiHost}/api/group/${groupId}/visibility`;
    let groupVisibility = "";
    let memberListVisibility = "";
    switch (groupVisibilityStatus) {
      case GroupVisibilityStatus.Public:
        groupVisibility = GroupVisibility.Searchable;
        memberListVisibility = MemberListVisibility.Public;
        break;
      case GroupVisibilityStatus.Private:
        groupVisibility = GroupVisibility.Searchable;
        memberListVisibility = MemberListVisibility.MembersOnly;
        break;
      case GroupVisibilityStatus.Secret:
        groupVisibility = GroupVisibility.Hidden;
        memberListVisibility = MemberListVisibility.MembersOnly;
        break;
      case GroupVisibilityStatus.Incognito:
        groupVisibility = GroupVisibility.Searchable;
        memberListVisibility = MemberListVisibility.AdminOnly;
        break;
    }
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          groupVisibility,
          memberListVisibility
        }
      })
    };
    return this.http.put<void>(url, null, options);
  }

  changeGroupMembershipOption(
    groupId: string,
    membershipOption: GroupMembershipOption,
    groupJoinByInvitationOnly?: boolean,
    domains?: string[]
  ): Observable<any> {
    const url = `${environment.apiHost}/api/group/${groupId}/membership/option`;
    const params: HttpParams = new HttpParams({
      fromObject: {
        membershipOption,
        groupJoinByInvitationOnly: groupJoinByInvitationOnly + ""
      }
    });
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params
    };
    return this.http.put(url, domains, options).pipe(
      catchError(errorRes => {
        this.alert.error("ExdrCommon.ServerError");
        throw errorRes;
      })
    );
  }

  changeGroupConversations(
    groupId: string,
    groupConversation: any
  ): Observable<void> {
    const url = `${environment.apiHost}/api/group/${groupId}/conversation/access`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    return this.http.post<void>(url, groupConversation, options);
  }
  changeGroupAnnouncers(
    groupId: string,
    groupAnnouncers: GroupAnnouncers
  ): Observable<void> {
    const url = `${environment.apiHost}/api/group/${groupId}/announcement/announcers`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          groupAnnouncers
        }
      })
    };
    return this.http.post<void>(url, null, options);
  }

  changeVisibilityOfMembersScore(
    groupId: string,
    isVisible: boolean
  ): Observable<void> {
    const url = `${environment.apiHost}/api/group/${groupId}/score`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          showMembersScore: isVisible + ""
        }
      })
    };
    return this.http.put<void>(url, null, options);
  }

  changeGroupName(groupId: string, groupName: string): Observable<void> {
    const url = `${environment.apiHost}/api/group/${groupId}/name`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          groupName
        }
      })
    };
    return this.http.post<void>(url, null, options);
  }

  createSmartContractTemplate(groupId: string, templateName: string) {
    const url = `${environment.apiHost}/api/group/${groupId}/contracttemplate`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          templateName
        }
      })
    };
    return this.http.post<void>(url, null, options);
    // https://idenediapi-prod-stag.azurewebsites.net/api/group/GHK1975083000/contracttemplate?templateName=test%20template
  }

  loadGroupContactTemplates(
    group: string
  ): Observable<SmartContractTemplateModel[]> {
    // const url = `/assets/mock/smart-contract.json`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    const url = `${environment.apiHost}/api/group/${group}/contracttemplate`;
    return this.http.get<SmartContractTemplateModel[]>(url, options).pipe(
      map(res =>
        res
          .map(template => new SmartContractTemplateModel(template))
          .sort((a, b) => {
            if (a.InstanceId === APP_DEFAULTS.OWNERSHIP_CONTRACT_ID) {
              return -1;
            }
            if (b.InstanceId === APP_DEFAULTS.OWNERSHIP_CONTRACT_ID) {
              return 1;
            }

            const dateA = moment(new Date(a.CreatedOn));
            const dateB = moment(new Date(b.CreatedOn));

            if (dateA.isBefore(dateB)) {
              return 1;
            } else if (dateB.isBefore(dateA)) {
              return -1;
            } else {
              return 0;
            }
          })
      ),
      catchError(errorRes => {
        this.alert.error("ExdrCommon.ServerError");
        throw errorRes;
      })
    );
  }

  getSmartContractById(groupId: string, smartContractID: string) {
    return this.loadGroupContactTemplates(groupId).pipe(
      map((data: []) => {
        if (data) {
          return data.find((sc: any) => sc.InstanceId === smartContractID);
        }
        return null;
      })
    );
  }

  updateCustomDomain(groupId: string, domainName: string, accessOption?: string) {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    let loginRequired;
    let publicOnly;
    let url;
    if (accessOption) {
      switch (accessOption) {
        case AccessOptions.LoginOptional:
          loginRequired = false;
          publicOnly = false;
          break;
        case AccessOptions.LoginRequired:
          loginRequired = true;
          publicOnly = false;
          break;
        case AccessOptions.PublicOnly:
          loginRequired = false;
          publicOnly = true;
          break;
      }
      url = `${environment.apiHost}/api/group/${groupId}/app/global/${STEMEXE_APP_ID}?customDomain=${domainName}&loginRequired=${loginRequired}&publicOnly=${publicOnly}`;
    }
    else {
      url = `${environment.apiHost}/api/group/${groupId}/app/global/${STEMEXE_APP_ID}?customDomain=${domainName}`;
    }

    return this.http.put(url, {}, options).pipe(
      catchError(errorRes => {
        if (errorRes.error?.IdenediExceptionType == 'NOT_AVAILABLE') {
          this.alert.error('Domain not available')
        } else {
          this.alert.error('ExdrCommon.ServerError');
        }
        throw errorRes;
      })
    );
  }

  setUpAccessOption(groupSettings: GroupSettings): AccessOptions {
    let accessStatus: AccessOptions;
    if (
      groupSettings.GroupApp?.LoginRequired === true
    ) {
      accessStatus = AccessOptions.LoginRequired;
    }
    else if (groupSettings?.GroupApp?.PublicOnly && groupSettings.GroupApp.PublicOnly === true) {
      accessStatus = AccessOptions.PublicOnly;
    }
    // }
    else {
      accessStatus = AccessOptions.LoginOptional;
    }
    return accessStatus;
  }

  addBuToSubgroup(groupId: string, subGroupId: string, businessUnitId: string): Observable<any> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    const url = `${environment.apiHost}/api/group/${groupId}/subgroup/${subGroupId}/subgroup/${businessUnitId}`;
    return this.http.put(url, {}, options);
  }

  removeMemberFromGroup(groupId: string): Observable<any> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    const url = `${environment.apiHost}/api/group/${groupId}/membership`;
    return this.http.delete<any>(url, options);
  }

  deleteBusinessUnitsFromSubgroup(groupId: string, subGroupId: string, businessUnitId: string): Observable<any> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    const url = `${environment.apiHost}/api/group/${groupId}/subgroup/${subGroupId}/subgroup/${businessUnitId}`;
    return this.http.delete<any>(url, options);
  }

  fetchBusinessUnitsAddedInSubgroups(groupId: string, subGroupId: string): Observable<GroupDetail[]> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    const url = `${environment.apiHost}/api/group/${groupId}/subgroup/${subGroupId}/subgroup`;
    return this.http
      .get<GroupDetail[]>(url, options)
      .pipe(
        map(subGroups => subGroups.map(subGroup => new GroupDetail(subGroup)))
      );
  }

  // fetchSubGroups(groupId: string, onlyMember: boolean = false, subGroupType: SubGroupType = SubGroupType.UserType): Observable<GroupDetail[]> {
  //   const options = {
  //     headers: this.authService.buildAuthHeader(AuthType.Idenedi),
  //   };
  //   options['params'] = new HttpParams({
  //     fromObject: {
  //       onlyifmember: onlyMember ? 'true' : 'false',
  //     }
  //   })
  //   const url = `${environment.apiHost}/api/group/${groupId}/subgroup/${subGroupType}`;
  //   // const url = `assets/mock/subgroups.json`;
  //   return this.http
  //     .get<GroupDetail[]>(url, options)
  //     .pipe(
  //       map(subGroups => subGroups.map(subGroup => new GroupDetail(subGroup)))
  //     );
  // }
  fetchSubGroups(
    groupId: string,
    onlyMember: boolean = false,
    subGroupType: SubGroupType = SubGroupType.UserType,
    addTimeStamp = false
  ): Observable<GroupDetail[]> {

    let params = new HttpParams().set('onlyifmember', onlyMember ? 'true' : 'false');

    if (addTimeStamp) {
      params = params.set('time', Date.now().toString());
    }

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: params
    };

    const url = `${environment.apiHost}/api/group/${groupId}/subgroup/${subGroupType}`;

    return this.http.get<GroupDetail[]>(url, options).pipe(
      map(subGroups => subGroups.map(sub => new GroupDetail(sub)))
    );
  }


  private mergeSubGroups(allGroups: GroupDetail[]): GroupDetail[] {
    const mergedGroups = [];
    const subGroups = [];
    allGroups.forEach(group => {
      if (!group.Idenedi.includes("_")) {
        group.MergedSeenAnnouncements = group.SeenAnnouncements || 0;
        group.MergedTotalAnnouncements =
          group?.GroupSummary?.TotalAnnouncements || 0;
        group.MergedUnseenAnnouncements = Math.max(
          group.MergedTotalAnnouncements - group.MergedSeenAnnouncements,
          0
        );
        mergedGroups.push(group);
      } else {
        subGroups.push(group);
      }
    });
    subGroups.forEach(subGroup => {
      const parentGroup = mergedGroups.find(
        g => g.Idenedi === subGroup.Idenedi.split("_")[0]
      );
      parentGroup.MergedSeenAnnouncements += subGroup.SeenAnnouncements || 0;
      parentGroup.MergedTotalAnnouncements +=
        subGroup?.GroupSummary?.TotalAnnouncements || 0;
      const subGroupUnseen = Math.max(
        (subGroup?.GroupSummary?.TotalAnnouncements || 0) -
        (subGroup?.SeenAnnouncements || 0),
        0
      );
      parentGroup.MergedUnseenAnnouncements += subGroupUnseen;
    });
    return mergedGroups;
  }

  public sortGroupsByName(unsortedGroups: GroupDetail[]): GroupDetail[] {
    return unsortedGroups.sort((a, b) => {
      const nameA = a.Profile.FirstName.toUpperCase();
      const nameB = b.Profile.FirstName.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }

  getInvitationKey(groupId: string): Observable<any> {
    const url = `${environment.apiHost}/api/group/${groupId}/invite`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<any>(url, {}, options);
  }

  getGoBackUrl() {
    return sessionStorage.getItem(this.GO_BACK_KEY)
  }
  setGoBackUrl(url: string) {
    sessionStorage.setItem(this.GO_BACK_KEY, url)
  }
  markAsStemexeGroup(groupId: string): Observable<string[]> {
    const url = `${environment.apiHost}/api/group/${groupId}/app/global/${STEMEXE_APP_ID}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.put<string[]>(url, {}, options);
  }
  fetchUserSubGroups(groupId: string, subGroupType: SubGroupType = SubGroupType.UserType): Observable<any[]> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          onlyifmember: 'true'
        }
      })
    };
    const url = `${environment.apiHost}/api/group/${groupId}/subgroup/id/${subGroupType}`;
    return this.http.get<string[]>(url, options).pipe(map(ids => {
      let allGroupsIds: string[] = [];
      allGroupsIds = ids;
      allGroupsIds.push(groupId)
      return allGroupsIds;
    }))
  }
  getGroupPassword(groupId: string): Observable<string> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    };
    const url = `${environment.apiHost}/api/group/${groupId}/app`;
    return this.http.post<string>(url, {}, options)
  }
  setGroupData(groupId: string) {
    groupId = groupId.split('_')[0]
    this.authService.idenediGroupId = groupId;
    AppMsalConfig.groupId = groupId;
    AppMsalConfig.accessToken = localStorage.getItem(AppMsalConfig.groupId + '_msalToken');
    sessionStorage.removeItem(ESP_LOGIN_DATA);
    this.plannexeAuthService.logout();
    localStorage.removeItem(LocalEnum.isloggedin);
    localStorage.removeItem(LocalEnum.user);
    localStorage.removeItem(LocalEnum.token);
    localStorage.removeItem("userTeam");
    localStorage.removeItem(SellexeLocalEnum.isloggedin);
    localStorage.removeItem(SellexeLocalEnum.user);
    localStorage.removeItem('SignInByIdenediToken');
  }

  getNotificationEmail(groupId: string): Observable<string> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    const url = `${environment.apiHost}/api/group/${groupId}/email`;
    return this.http.get<string>(url, options)
  }

  getNotificationPhoneNumber(groupId: string): Observable<string> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    const url = `${environment.apiHost}/api/group/${groupId}/notify`;
    return this.http.get<string>(url, options)
  }

  updateNotificationPhoneNumber(groupId: string, phoneNumber: string, code: string): Observable<string> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    const url = `${environment.apiHost}/api/group/${groupId}/notify/phone`;
    const data = {
      Phone: phoneNumber,
      Code: code
    }
    return this.http.put<string>(url, data, options);
  }

  updateAuthorizationNumber(newPhoneNumber: string, code: string, password: string, region: string) {
    let url = `${environment.idenediProviderUrl}/api/account/ChangeAuthorizationNumber`;
    const authHeaders = this.authService.buildAuthHeader(AuthType.Idenedi);
    const options = {
      headers: authHeaders.append("Content-Type", "application/json")
    };
    const data = {
      NewPhoneNo: newPhoneNumber,
      Password: password,
      VerificationCode: code,
      Region: region
    }
    return this.http.post<any>(url, data, options);
  }

  updateNotificationEmail(groupId: string, email: string, code: string): Observable<string> {
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    const url = `${environment.apiHost}/api/group/${groupId}/email`;
    const data = {
      Email: email,
      Key: code
    }
    return this.http.put<string>(url, data, options);
  }

  updateNotificationPreference(groupId: string, preference: string): Observable<string> {
    const headers = this.authService.buildAuthHeader(AuthType.Idenedi)
    const options = {
      headers: headers.append('content-type', 'application/json; charset=utf-8')
    }
    const url = `${environment.apiHost}/api/group/${groupId}/notify/pref`;
    return this.http.put<string>(url, `"${preference}"`, options);
  }


  createSubgroup(
    groupIdenedi: string,
    groupName: string,
    memberIdenedis?: string[],
    subGroupType: SubGroupType = SubGroupType.UserType
  ): Observable<string> {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/subgroup/${subGroupType}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    const body: {
      GroupName: string;
      MemberIdenedis?: string[];
    } = {
      GroupName: groupName
    };
    if (memberIdenedis) {
      body.MemberIdenedis = memberIdenedis;
    }
    return this.http.post<any>(url, body, options);
  }

  changeGroupProfileImage(groupIdenedi: string, file) {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/profileimage`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    options.headers.append('content-type', 'multipart/form-data')
    const body: FormData = new FormData();
    body.append('file', file);
    return this.http.post<any>(url, body, options);

  }

  deleteProfileImage(groupIdenedi: string) {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/profileimage`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };

    return this.http.delete<any>(url, options);

  }

  updateMembershipOption(groupIdenedi: string, inviteOnly: boolean, membershipOption: GroupMembershipOption, domains: string[], countries: string, requiredChannels = '', GroupJoinShowOnLogin = false) {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/membership/option`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          membershipOption: membershipOption,
          groupJoinByInvitationOnly: inviteOnly,
          allowedCountries: countries,
          requiredChannels: requiredChannels || ',',
          GroupJoinShowOnLogin: GroupJoinShowOnLogin
        }
      })
    };
    return this.http.put<any>(url, domains, options);
  }
  getAllExternalProviders(groupIdenedi) {
    const url = `${environment.apiHost}/api/${groupIdenedi}/externalIdProvider?getAll=true`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.get<any>(url, options);
  }

  addProvider(groupIdenedi: string, type: string, provider: any) {
    const url = `${environment.apiHost}/api/${groupIdenedi}/externalIdProvider/${type}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<any>(url, provider, options);
  }
  toggleProvider(groupIdenedi: string, providerId: string, isActive: boolean) {
    const url = `${environment.apiHost}/api/${groupIdenedi}/externalIdProvider/${providerId}/status`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.put<any>(url, isActive, options);
  }
  deleteProvider(groupIdenedi: string, providerId: string) {
    const url = `${environment.apiHost}/api/${groupIdenedi}/externalIdProvider/${providerId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.delete<any>(url, options);
  }

  updateProvider(groupIdenedi: string, providerId: string, provider) {
    const url = `${environment.apiHost}/api/${groupIdenedi}/externalIdProvider/${providerId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.put<any>(url, provider, options);
  }

  getGroupUrl() {
    return this.getGroupDetailsPublic(this.authService.idenediGroupId).pipe(map(group => {
      return group?.GroupSettings?.GroupApp?.CustomDomain;
    }));
  }

  hasFullControl(userIdenedi: string) {
    return this.getGroupDetailsPublic(userIdenedi)?.pipe(map(group => {
      return group.GroupAdminRoles.some(role => role == AdminRole.FullControl)
    })) ?? of(false);
  }

  getSmartContactDetails(groupId: string, memberIdenedi: string) {
    const url = `${environment.apiHost}/api/group/${groupId}/member/${memberIdenedi}/contract`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.get<any>(url, options);
  }

  getSmartContactDetail(groupId: string, contractId: string) {

    const url = `${environment.apiHost}/api/group/${groupId}/contract/${contractId}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.get<any>(url, options);
  }

  getSmartContact(key: string, groupId: string) {
    const url = `${environment.apiHost}/api/group/${groupId}/contract/share?shareKey=${key}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.get<any>(url, options);
  }

  acceptSmartContact(groupId: string, contractId: string, key: string, status) {
    const url = `${environment.apiHost}/api/group/${groupId}/contract/${contractId}/status?newStatus=${status}&shareKey=${key}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<any>(url, {}, options);
  }

  acceptSmartContactWithOutShareKey(groupId: string, contractId: string, status) {
    const url = `${environment.apiHost}/api/group/${groupId}/contract/${contractId}/status?newStatus=${status}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<any>(url, {}, options);
  }

  rejectSmartContactWithOutShareKey(groupId: string, contractId: string, status) {
    const url = `${environment.apiHost}/api/group/${groupId}/contract/${contractId}/status?newStatus=${status}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<any>(url, {}, options);
  }
  rejectSmartContact(groupId: string, contractId: string, key: string, status) {
    const url = `${environment.apiHost}/api/group/${groupId}/contract/${contractId}/status?newStatus=${status}&shareKey=${key}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.post<any>(url, {}, options);
  }
  getUserDetail(idenedi: string) {
    return this.http.get<any>(
      `${environment.apiHost}/api/user/${idenedi}`,
    );
  }
  getDefinitionApplicationInfo(groupGuid: string) {
    return this.http.get<any>('/idenediTenant/new/subtenant/' + groupGuid, { observe: 'response', headers: this.authService.buildAuthHeader(AuthType.ESP) });
  }



  // pdf generated working
  // Method to initiate PDF generation
  printData(sectionId: string, productDetails: any): void {
    // const element = document.getElementById(sectionId);
    const element = document.getElementById(sectionId);
    if (!element) {
      console.error('Section not found');
      return;
    }

    // Find all images within the section and convert them to Base64
    const images = element.querySelectorAll('img');
    // .map((img) => this.convertBlobImageToBase64(img))
    const imagePromises = Array.from(images);

    const innerHtml = element?.innerHTML;

    // Once all images are converted, generate the PDF
    Promise.all(imagePromises)
      .then(() => {
        this.createConditionalPDF(element, productDetails);
      })
      .catch((error) => {
        console.error('Error converting images to Base64:', error);
      });
  }

  // Function to convert Blob image to Base64
  convertBlobImageToBase64(img: HTMLImageElement): Promise<void> {
    return new Promise((resolve, reject) => {
      const imgURL = img.src; // Get the image URL
      fetch(imgURL)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => {
            img.src = reader.result as string; // Replace the image src with the Base64 string
            resolve(); // Resolve the promise once conversion is complete
          };
          reader.onerror = () => reject('Error reading the Blob data.');
          reader.readAsDataURL(blob); // Convert blob to Base64
        })
        .catch(error => reject(`Error fetching the image Blob: ${error}`));
    });
  }

  // Function to create the PDF conditionally based on renderType
  createConditionalPDF(element: HTMLElement, productDetails: any): void {
    // let options: any;

    // if (productDetails?.RenderType === 'Connected' || productDetails?.RenderType == 'FullWidthConnectedSlides') {
    //   const elementWidthInPx = element.scrollWidth; // Get the width of the content in pixels
    //   const elementHeightInPx = element.scrollHeight; // Get the height of the content in pixels
    //   const pxToPtRatio = 0.75; // Conversion ratio from pixels to points (0.063rem = 0.75pt)
    //   const pdfWidth = elementWidthInPx * pxToPtRatio; // Convert content width to points
    //   const pdfHeight = elementHeightInPx * pxToPtRatio; // Convert content height to points

    //   const options = {
    //     margin: [0, 15, 0, 15], // Equal margins on all sides (top, left, bottom, right) in points
    //     filename: productDetails.CategoryName,
    //     image: { type: 'jpeg', quality: 1 },
    //     html2canvas: { scale: 2, useCORS: true, logging: true }, // Increase the scale for better quality
    //     jsPDF: { unit: 'pt', format: [pdfWidth, pdfHeight], orientation: 'portrait' }, // Custom page size
    //     pagebreak: { mode: [] } // Ensure no page breaks are used
    //   };

    //   // Generate and save the PDF using the customized page size and consistent margins
    //   html2pdf().from(element).set(options).save();

    // } else {
    //   // Multi-page layout with page breaks for each section
    //   options = {
    //     margin: 0.1,
    //     filename: productDetails.CategoryName,
    //     image: { type: 'jpeg', quality: 1 },
    //     html2canvas: { scale: 3, useCORS: true },
    //     jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    //     pagebreak: { mode: ['avoid-all', 'css', 'legacy'] } // Enable page breaks for Default renderType
    //   };

    //   // Generate and save the PDF using html2pdf.js
    //   html2pdf()
    //     .from(element)
    //     .set(options)
    //     .toPdf()
    //     .get('pdf')
    //     .then((pdf) => {
    //       // Adjust the PDF pages based on content height for dynamic page sizing
    //       const totalPages = pdf.internal.getNumberOfPages();
    //       for (let i = 1; i <= totalPages; i++) {
    //         pdf.setPage(i);
    //         const pageHeight = pdf.internal.pageSize.getHeight();
    //         const sectionHeight = pdf.internal.pageSize.height;
    //         pdf.internal.pageSize.height = sectionHeight < pageHeight ? sectionHeight : pageHeight;
    //       }
    //     })
    //     .save();
    // }
  }
  clearLoginAsData(): void {
    if (localStorage.getItem('loginAs')) {
      console.log("Removing 'loginAs' from local storage...");
      localStorage.removeItem('loginAs');
    }
    if (localStorage.getItem('loginAsType')) {
      console.log("Removing 'loginAsType' from local storage...");
      localStorage.removeItem('loginAsType');
    }
  }

  /**
   * Sets the target audience for a given group
   * @param groupId The unique identifier of the group
   * @param groupTargetAudience The target audience value
   * @returns Observable<any>
   */
  setGroupTargetAudience(groupId: string, groupTargetAudience: string): Observable<any> {
    const url = `${environment.apiHost}/api/group/${groupId}/audience/type?groupTargetAudience=${groupTargetAudience}`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };
    return this.http.put<any>(url, {}, options);
  }

  /**
   * Cretes a subtenant for a given group
   * @param groupId The unique identifier of the group
   * @param body The body of the request
   * @returns Observable<any>
   */
  createSubTenant(groupId: string, body: any): Observable<any> {
    // Construct the final endpoint using the groupId parameter
    const url = `${environment.apiHost}/api/group/${groupId}/subgroup/SubTenant`;

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };

    return this.http.post<any>(url, body, options);
  }

  isGroupAdmin() {
    return this.groupAdmin
  }

  blockMember(
    groupIdenedi: string,
    memberIdenedi: string
  ): Observable<void> {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/member/${memberIdenedi}/block`;

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          groupIdenedi,
          memberIdenedi
        }
      })
    };
    return this.http.put<void>(url, null, options);
  }

  unBlockMember(
    groupIdenedi: string,
    memberIdenedi: string
  ): Observable<void> {
    const url = `${environment.apiHost}/api/group/${groupIdenedi}/member/${memberIdenedi}/unblock`;

    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi),
      params: new HttpParams({
        fromObject: {
          groupIdenedi,
          memberIdenedi
        }
      })
    };
    return this.http.put<void>(url, null, options);
  }

  syncProvider(groupIdenedi: string, instanceId: string): Observable<any> {
    const url = `${environment.apiHost}/api/${groupIdenedi}/externalIdProvider/${instanceId}/sync`;
    const options = {
      headers: this.authService.buildAuthHeader(AuthType.Idenedi)
    };

    // const options = {
    //   headers: this.authService.buildAuthHeader(AuthType.Idenedi),
    //   // params: new HttpParams().set('client_id', clientId).set('gIdenedi', gIdenedi)
    // };

    return this.http.post<any>(`${url}`, {}, options);

    return this.http.post<any>(url, {}, options);
  }

  get groupFullName() {
    return this._groupFullName;
  }

  get isExceeders() {
    return environment.build == ENV_BUILD.EXCEEDERS
  }

  get isEnableGroupSelection() {
    return environment.enableGroupsSelection
  }
}
